/**
 * 매뉴얼 레지스트리
 *
 * 새 매뉴얼을 추가하려면:
 *   1) public/manuals/{id}.html 에 매뉴얼 HTML 파일을 올린다. (독립 실행되는 완전한 HTML)
 *   2) 아래 MANUALS 배열에 항목을 추가하고 status 를 'ready' 로 바꾼다.
 *
 * 준비 중인 매뉴얼은 status: 'planned' 로 두면 목록에 "준비 중"으로 표시되고
 * 클릭이 막힌다. 파일이 준비되면 status 만 'ready' 로 바꾸면 된다.
 *
 * group 은 목록 화면에서 메뉴별로 묶는 데 쓰인다. GROUP_ORDER 순서대로 표시된다.
 */

/** @typedef {'ready'|'planned'} ManualStatus */

export const MANUAL_STATUS = {
  READY: 'ready',
  PLANNED: 'planned',
};

/** 목록에 표시할 그룹 순서 */
export const GROUP_ORDER = ['상품 관리', '예약 관리', '상담 관리'];

export const MANUALS = [
  // ── 상품 관리 ──────────────────────────────────────────────
  {
    id: 'tour-product-registration',
    group: '상품 관리',
    title: '투어 상품 등록',
    summary: '새 상품 등록과 스마트 상품 등록, 두 가지 방법의 차이와 사용법',
    menuLabel: '상품 관리',
    menuPath: '/products/tour',
    status: MANUAL_STATUS.READY,
    updatedAt: '2026-08-14',
    chapters: [
      '등록 방법 두 가지',
      '어디서 시작하나',
      '새 상품 등록',
      '스마트 등록 1단계',
      '스마트 등록 2단계',
      '등록 후 할 일',
    ],
  },
  {
    id: 'product-basic-settings',
    group: '상품 관리',
    title: '기본 설정',
    summary: '예약 확정 방식, 마감 기준, 신청금·완납 규칙과 상품 복사',
    menuLabel: '상품 관리',
    menuPath: '/products/tour',
    status: MANUAL_STATUS.READY,
    updatedAt: '2026-08-14',
    chapters: [
      '상품 기본 정보',
      '예약 설정',
      '결제 설정',
      '예약시 받을 정보',
      '상품 복사',
    ],
  },
  {
    id: 'product-price-stock',
    group: '상품 관리',
    title: '가격 / 재고',
    summary: '날짜별 판매가와 잔여 좌석 관리, 스마트 가격 일괄 등록',
    menuLabel: '상품 관리',
    menuPath: '/products/tour',
    status: MANUAL_STATUS.READY,
    updatedAt: '2026-08-14',
    chapters: [
      '재고구분과 요금제',
      '날짜 고르기',
      '판매가 넣기',
      '재고와 마감',
      '스마트 가격 등록',
    ],
  },
  {
    id: 'shared-inventory',
    group: '상품 관리',
    title: '공유재고',
    summary: '여러 상품이 같은 좌석을 나눠 쓰는 공유재고 설정과 주의사항',
    menuLabel: '공유재고 관리',
    menuPath: '/settings/shared-inventory',
    status: MANUAL_STATUS.READY,
    updatedAt: '2026-09-03',
    chapters: [
      '공유재고가 뭔가요',
      '전체 흐름',
      '목록 화면 둘러보기',
      '시리즈 만들기',
      '좌석 넣기',
      '상품에 연결하기',
      '달력에서 읽는 법',
    ],
  },
  {
    id: 'product-detail-page',
    group: '상품 관리',
    title: '상세페이지 설정',
    summary: '손님에게 보이는 상품 페이지 구성 — 대표이미지, 일정표, 포함/불포함, 유의사항',
    menuLabel: '상품 관리',
    menuPath: '/products/tour',
    status: MANUAL_STATUS.READY,
    updatedAt: '2026-08-14',
    chapters: [
      '대표이미지',
      '미팅장소',
      '상품 상세 정보',
      '일정표 만들기',
      '확인하고 내보내기',
    ],
  },

  // ── 예약 관리 ──────────────────────────────────────────────
  {
    id: 'reservation-management',
    group: '예약 관리',
    title: '예약관리',
    summary: '예약과 단체의 차이부터 입금 처리, 비용 등록, 정산서 발행까지',
    menuLabel: '예약 관리',
    menuPath: '/settlements',
    status: MANUAL_STATUS.READY,
    updatedAt: '2026-08-14',
    chapters: [
      '예약과 단체의 차이',
      '예약관리 화면 둘러보기',
      '조회 탭 세 개',
      '예약 상세',
      '단체 상세',
      '입금 처리',
      '비용(전도금) 등록',
      '정산하기',
    ],
  },

  // ── 상담 관리 ──────────────────────────────────────────────
  {
    id: 'consult-management',
    group: '상담 관리',
    title: '상담관리',
    summary: '견적 접수부터 원가·판매가 계산, 견적서 발송, 예약전환까지',
    menuLabel: '상담 관리',
    menuPath: '/quotes',
    status: MANUAL_STATUS.READY,
    updatedAt: '2026-08-14',
    chapters: [
      '상담관리가 하는 일',
      '목록 화면 둘러보기',
      '신규 견적 등록',
      '견적 상세 화면',
      '원가와 판매가',
      '견적서 만들어 보내기',
      '상담상태와 예약전환',
      '비교견적서',
    ],
  },
];

/** 매뉴얼 HTML 파일 경로 */
export const getManualFileUrl = (id) => `${process.env.PUBLIC_URL || ''}/manuals/${id}.html`;

/** id 로 매뉴얼 찾기 */
export const findManual = (id) => MANUALS.find((m) => m.id === id) || null;

/** 읽을 수 있는(파일이 준비된) 매뉴얼만 */
export const getReadyManuals = () => MANUALS.filter((m) => m.status === MANUAL_STATUS.READY);

/** 그룹별로 묶어서 반환 — [{ group, items }] */
export const getGroupedManuals = () => {
  const buckets = new Map();
  MANUALS.forEach((m) => {
    const key = m.group || '기타';
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(m);
  });

  const ordered = [];
  GROUP_ORDER.forEach((key) => {
    if (buckets.has(key)) {
      ordered.push({ group: key, items: buckets.get(key) });
      buckets.delete(key);
    }
  });
  // GROUP_ORDER 에 없는 그룹은 뒤에 붙인다
  buckets.forEach((items, key) => ordered.push({ group: key, items }));
  return ordered;
};

export default MANUALS;
